/* eslint-disable jsx-a11y/anchor-is-valid */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import update from 'immutability-helper';
import React, { useEffect, useMemo, useState } from 'react';
import {
    IOption,
    IOrderDetails,
    IProductDetail,
    IReceiverPart,
} from '../../../models/product-detail';
import { findIcon, noReceiverOption } from '../../common/constants';
import SelectInput from '../../inputs/select-input';
import TextInput from '../../inputs/text-input';
import { ProductOrderFormErrors } from './validation';
import { useIntl } from 'react-intl';
import { ReceiverType } from '../../common/constants';
import { getGainId } from '../../../hooks/gain-translate-hook';

interface IReceiversSelect {
    productState: IProductDetail;
    gains: IOption[];
    cables: IOption[];
    updateReceivers: (receivers: IReceiverPart[]) => void;
    quantity: number;
    receivers: IReceiverPart[];
    orderState: IOrderDetails;
    receiver: IReceiverPart | null;
    errors: ProductOrderFormErrors;
    countryCode: string;
    isAdd: boolean;
}

const ReceiversSelect = ({
    productState,
    gains,
    cables,
    quantity,
    receivers,
    updateReceivers,
    orderState,
    receiver,
    errors,
    countryCode,
    isAdd,
}: IReceiversSelect) => {
    const intl = useIntl();
    const [receiversNeeded, setReceiversNeeded] = useState(0);
    const [noReceiver, setNoReceiver] = useState(false);
    const quantityCheck = () => {
        if (
            orderState!.receivers!.every((x) => {
                return x.quantity !== undefined;
            }) &&
            !isNaN(
                orderState!.receivers!.reduce(
                    (x, y) => x + parseInt(y.quantity!.toString()),
                    0
                )
            ) &&
            !isNaN(orderState!.quantity)
        ) {
            setReceiversNeeded(
                orderState!.quantity -
                    orderState!.receivers!.reduce(
                        (x, y) => x + parseInt(y.quantity!.toString()),
                        0
                    )
            );
        }
    };

    const addReceiver = () => {
        if (productState.isSnapFit2) {
            updateReceivers(
                update(orderState!.receivers!, {
                    $push: [
                        {
                            ...receiver!,
                            useAngledParts: false,
                            quantity: 0,
                        },
                    ],
                })
            );
        } else {
            updateReceivers(
                update(orderState!.receivers!, {
                    $push: [
                        {
                            ...receiver!,
                            useAngledParts: true,
                            quantity: 0,
                        },
                    ],
                })
            );
        }
    };

    const deleteReceiver = (at: number) => {
        let newReceivers = update(orderState!.receivers!, {
            $splice: [[at, 1]],
        });
        if (newReceivers.length === 0) {
            newReceivers = update(newReceivers, {
                $push: [
                    {
                        ...receiver!,
                        useAngledParts: true,
                        quantity: 0,
                    },
                ],
            });
        }
        updateReceivers(newReceivers);
    };

    const updateReceiver = (event: any, index: number) => {
        switch (event.target.name) {
            case 'GainSelection':
                if (productState.isSnapFit2) {
                    updateReceivers(
                        update(receivers, {
                            [index]: {
                                gain: {
                                    $set: event.target.value
                                        ? event.target.value
                                        : undefined,
                                },
                                partType: {
                                    $set: ReceiverType.SnapFit2,
                                },
                            },
                        })
                    );
                } else if (productState.isSnapfit) {
                    updateReceivers(
                        update(receivers, {
                            [index]: {
                                gain: {
                                    $set: event.target.value
                                        ? event.target.value
                                        : undefined,
                                },
                                partType: {
                                    $set: ReceiverType.SnapFit2,
                                },
                            },
                        })
                    );
                } else {
                    updateReceivers(
                        update(receivers, {
                            [index]: {
                                gain: {
                                    $set: event.target.value
                                        ? event.target.value
                                        : undefined,
                                },
                                partType: {
                                    $set: ReceiverType.NonSnapFit,
                                },
                            },
                        })
                    );
                }

                break;
            case 'EarSelection':
                updateReceivers(
                    update(receivers, {
                        [index]: {
                            side: {
                                $set: event.target.value
                                    ? event.target.value
                                    : undefined,
                            },
                        },
                    })
                );
                break;
            case 'CableLengthSelection': {
                const newLength = parseInt(event.target.value);

                if (newLength === noReceiverOption) {
                    setNoReceiver(true);
                } else {
                    setNoReceiver(false);
                }

                updateReceivers(
                    update(receivers, {
                        [index]: {
                            cableLength: {
                                $set: newLength ? newLength : undefined,
                            },
                        },
                    })
                );
                break;
            }
            case 'noOfReceivers': {
                const newQuantity = parseInt(event.target.value);
                updateReceivers(
                    update(receivers, {
                        [index]: {
                            quantity: {
                                $set: newQuantity ? newQuantity : 0,
                            },
                        },
                    })
                );
                break;
            }
            default:
                break;
        }
    };

    useEffect(() => {
        if (orderState!.receivers) quantityCheck();
    }, [orderState!]);

    const getDisabled = (receiver: IReceiverPart) => {
        if (receiver && receiver!.cableLength === noReceiverOption) {
            return true;
        }
        return false;
    };

    const shouldShow =
        orderState!.orderType !== 'patient' ||
        (orderState.additionalReceivers && isAdd);

    return (
        <div>
            {receivers &&
                receivers.map((receiver, index) => {
                    return (
                        <div key={index}>
                            <div className="row">
                                {shouldShow ? (
                                    <div className="col-sm-3">
                                        <SelectInput
                                            name="EarSelection"
                                            onChange={(event: any) =>
                                                updateReceiver(event, index)
                                            }
                                            value={receiver.side}
                                            label="Ear"
                                            options={[
                                                {
                                                    value: 'L',
                                                    text: intl.formatMessage({
                                                        id: 'Left',
                                                        defaultMessage: 'Left',
                                                    }),
                                                },
                                                {
                                                    value: 'R',
                                                    text: intl.formatMessage({
                                                        id: 'Right',
                                                        defaultMessage: 'Right',
                                                    }),
                                                },
                                            ]}
                                            hasDefaultOption={true}
                                            defaultOption={'Select Ear'}
                                            labelClassName="col-sm-12"
                                            fieldClassName="col-sm-12"
                                            inlineStyle={{
                                                backgroundColor: '#f4f4f4',
                                                height: '40px',
                                                borderRadius: '0',
                                            }}
                                            error={
                                                !receiver.side && !noReceiver
                                                    ? errors.receivers
                                                    : undefined
                                            }
                                            disabled={getDisabled(receiver)}
                                        />
                                    </div>
                                ) : (
                                    <div className="col-sm-3">
                                        {receiver.side && (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={findIcon(
                                                        'fal',
                                                        'ear'
                                                    )}
                                                    className={`ear-icon-${
                                                        receiver.side === 'L'
                                                            ? 'blue'
                                                            : 'red'
                                                    }`}
                                                />{' '}
                                                <p className="ear-mention">
                                                    {receiver.side === 'L'
                                                        ? 'Left'
                                                        : 'Right'}{' '}
                                                    {intl.formatMessage({
                                                        id: 'Ear',
                                                        defaultMessage: 'Ear',
                                                    })}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                )}
                                <div className="col-sm-3">
                                    <SelectInput
                                        name="CableLengthSelection"
                                        onChange={(event: any) =>
                                            updateReceiver(event, index)
                                        }
                                        value={receiver.cableLength}
                                        label="Cable Length"
                                        options={cables.map((cableLength) => ({
                                            value:
                                                cableLength.name ===
                                                intl.formatMessage({
                                                    id: 'No_Receiver',
                                                })
                                                    ? 10
                                                    : parseInt(
                                                          cableLength.name.replace(
                                                              /^\D+/g,
                                                              ''
                                                          )
                                                      ),
                                            text: cableLength.name,
                                        }))}
                                        hasDefaultOption={true}
                                        defaultOption={'Select Cable Length'}
                                        labelClassName="col-sm-12"
                                        fieldClassName="col-sm-12"
                                        inlineStyle={{
                                            backgroundColor: '#f4f4f4',
                                            height: '40px',
                                            borderRadius: '0',
                                        }}
                                        error={
                                            !receiver.cableLength
                                                ? errors.receivers
                                                : undefined
                                        }
                                    />
                                </div>
                                {/* Hide gain selection for cros Transmitter selection */}
                                {orderState.rightSelectedIds &&
                                    !orderState.rightSelectedIds.includes(
                                        510
                                    ) &&
                                    !orderState.leftSelectedIds.includes(
                                        510
                                    ) && (
                                        <div className="col-sm-3">
                                            <SelectInput
                                                name="GainSelection"
                                                onChange={(event: any) =>
                                                    updateReceiver(event, index)
                                                }
                                                value={receiver.gain}
                                                label="Gain"
                                                options={gains.map((gain) => ({
                                                    value: gain.english,
                                                    text: intl.formatMessage({
                                                        id: getGainId(
                                                            gain.english
                                                        ),
                                                        defaultMessage:
                                                            gain.name,
                                                    }),
                                                }))}
                                                hasDefaultOption={true}
                                                defaultOption={'Select Gain'}
                                                labelClassName="col-sm-12"
                                                fieldClassName="col-sm-12"
                                                inlineStyle={{
                                                    backgroundColor: '#f4f4f4',
                                                    height: '40px',
                                                    borderRadius: '0',
                                                }}
                                                error={
                                                    !receiver.gain &&
                                                    !noReceiver
                                                        ? errors.receivers
                                                        : undefined
                                                }
                                                disabled={getDisabled(receiver)}
                                            />
                                        </div>
                                    )}
                                {shouldShow ? (
                                    <div className="col-sm-3">
                                        <TextInput
                                            name="noOfReceivers"
                                            onChange={(event: any) =>
                                                updateReceiver(event, index)
                                            }
                                            value={receiver.quantity}
                                            label="Quantity"
                                            labelClassName="col-sm-12"
                                            fieldClassName="col-sm-6"
                                            elementClassName="pdp-input-text"
                                            error={
                                                !receiver.quantity &&
                                                !noReceiver
                                                    ? errors.receivers
                                                    : undefined
                                            }
                                            disabled={getDisabled(receiver)}
                                        />

                                        <div className=" col-sm-1">
                                            <a
                                                onClick={() =>
                                                    deleteReceiver(index)
                                                }
                                                className="far fa-window-close close-button"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="col-sm-3" />
                                )}
                            </div>
                        </div>
                    );
                })}
            <div className="row">
                <div className="col-md-12 col-sm-12">
                    {shouldShow && (
                        <>
                            {!['US', 'CA', 'FR', 'DE'].includes(
                                countryCode
                            ) && (
                                <div
                                    style={{
                                        marginTop: '5px',
                                        fontSize: '15px',
                                    }}
                                >
                                    <i>
                                        {intl.formatMessage({
                                            id: 'The_number_of_receivers_should_be_less_than_or_equal_to_your_total_bulk_quantity',
                                            defaultMessage: `The number of receivers should be less than
                                        or equal to your total bulk quantity.`,
                                        })}
                                    </i>
                                    <i
                                        className={
                                            receiversNeeded < 0
                                                ? 'required-red pull-right'
                                                : 'pull-right'
                                        }
                                    >
                                        {receiversNeeded >= 0
                                            ? ` ${receiversNeeded} receiver${
                                                  receiversNeeded !== 1
                                                      ? 's'
                                                      : ''
                                              } left`
                                            : ` ${-receiversNeeded} receiver${
                                                  -receiversNeeded !== 1
                                                      ? 's'
                                                      : ''
                                              } too many`}
                                    </i>
                                </div>
                            )}
                            {!noReceiver && (
                                <div>
                                    <h5>
                                        <a
                                            onClick={addReceiver}
                                            id="product-details-add-another-link"
                                        >
                                            {intl.formatMessage({
                                                id: 'Add_Another',
                                                defaultMessage: 'Add Another',
                                            })}
                                        </a>
                                    </h5>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReceiversSelect;
